import { ConfiguratorGroupDto } from '@/services/configurator';
import { ConfiguratorVisualizationRoomDto } from './configurator-visualization.types';

// The configurator treats a param value starting with "missing" as "no option selected here". This is
// the fallback we send for a mapped group that can't resolve to a real option when the param itself
// defines no explicit missing sentinel.
const MISSING_VALUE = 'missing';

/**
 * Builds the CloudFront visualization iframe URL for a room from its mapping and the buyer's current selections:
 *   room = the room's configuratorMappingValue
 *   per mapping (key = configuratorMappingValue):
 *     - optionGroupId null                       -> defaultOptionConfiguratorMappingValue (no group maps to this visual)
 *     - selected item maps to an option          -> that option's configuratorMappingValue
 *     - nothing selected, standard item mapped    -> the standard (isDefault) item's configuratorMappingValue
 *     - mapped group, effective item has no value -> missingOptionConfiguratorMappingValue (or the literal "missing")
 */
export function buildVisualizationUrl(vizRoom: ConfiguratorVisualizationRoomDto, groups: ConfiguratorGroupDto[]): string {
    const params = new URLSearchParams();

    if (vizRoom.configuratorMappingValue) {
        params.set('room', vizRoom.configuratorMappingValue);
    }

    for (const mapping of vizRoom.mappings) {
        let value: string | null;

        if (mapping.optionGroupId == null) {
            // No option group maps to this visual at all — use the mapping's own default.
            value = mapping.defaultOptionConfiguratorMappingValue;
        } else {
            const group = groups.find((g) => g.id === mapping.optionGroupId);
            const selectedId = group?.selectedOption?.id ?? group?.options.find((o) => o.selected)?.id ?? null;
            // When nothing is selected, fall back to the group's standard (isDefault) option item, so an
            // untouched group still renders its standard look rather than jumping straight to "missing".
            const effectiveId = selectedId ?? group?.options.find((o) => o.isDefault)?.id ?? null;
            const matched = effectiveId != null ? mapping.options.find((o) => o.optionItemId === effectiveId) : undefined;
            // The group is mapped but the effective item (selection or standard) has no configurator
            // mapping value — tell the configurator the option is missing, falling back to the literal
            // "missing" token when the param defines no explicit sentinel. The param is never dropped so
            // the configurator can't silently render its default visual.
            value = matched ? matched.configuratorMappingValue : mapping.missingOptionConfiguratorMappingValue || MISSING_VALUE;
        }

        if (value) {
            params.set(mapping.configuratorMappingValue, value);
        }
    }

    return `${vizRoom.baseUrl}?${params.toString()}`;
}

/**
 * Builds the visualization URLs the buyer would land on by selecting each option of `group`, keeping
 * all other groups at their current selections. Used to pre-load configurator visuals when a group is
 * expanded, so the image swaps in instantly once an option is actually picked.
 */
export function buildGroupOptionVisualizationUrls(
    vizRoom: ConfiguratorVisualizationRoomDto,
    groups: ConfiguratorGroupDto[],
    group: ConfiguratorGroupDto
): string[] {
    return group.options.map((option) => {
        const simulatedGroups = groups.map((g) => (g.id === group.id ? { ...g, selectedOption: option } : g));
        return buildVisualizationUrl(vizRoom, simulatedGroups);
    });
}
